import { usePageAction } from '@repo/hooks/usePageAction';
import { useCallback } from 'react';

/**
 * Custom hook that provides event handlers for the read more/less functionality.
 * Handles click and keyboard events, tracks page actions, and toggles the checkbox state.
 *
 * @param idSuffix - Unique identifier suffix for the checkbox toggle element
 * @returns Object containing event handlers for read more and read less actions
 */
export const useCustomAboveSectionsToggleHandlers = (idSuffix: string) => {
  const fireReadMorePageAction = usePageAction({
    actionName: 'simplified_ru_read_more',
    actionSection: 'banking',
  });

  const fireReadLessPageAction = usePageAction({
    actionName: 'simplified_ru_show_less',
    actionSection: 'banking',
  });

  const handleReadMoreClick = useCallback(() => {
    fireReadMorePageAction();
  }, [fireReadMorePageAction]);

  const handleReadMoreKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        fireReadMorePageAction();
        const checkbox = document?.getElementById(`checkbox-toggle-${idSuffix}`);
        checkbox?.click();
        requestAnimationFrame(() => {
          checkbox?.parentElement?.querySelector<HTMLElement>('.read-less-container')?.focus();
        });
      }
    },
    [fireReadMorePageAction, idSuffix],
  );

  const handleReadLessClick = useCallback(() => {
    fireReadLessPageAction();
  }, [fireReadLessPageAction]);

  const handleReadLessKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        fireReadLessPageAction();
        const checkbox = document?.getElementById(`checkbox-toggle-${idSuffix}`);
        checkbox?.click();
        requestAnimationFrame(() => {
          checkbox?.parentElement?.querySelector<HTMLElement>('.read-more-label')?.focus();
        });
      }
    },
    [fireReadLessPageAction, idSuffix],
  );

  return {
    handleReadMoreClick,
    handleReadMoreKeyDown,
    handleReadLessClick,
    handleReadLessKeyDown,
  };
};
